import React, { useState, useEffect } from 'react';
import { 
  FaPhone, 
  FaFacebookF, 
  FaInstagram, 
  FaTwitter, 
  FaPinterest,
  FaBars,
  FaTimes,
  FaEnvelope,
  FaUser,
  FaGlobe,
  FaComments,
  FaCheckCircle,
  FaWhatsapp,
  FaSms,
  FaMapMarkerAlt,
  FaClock,
  FaHeadset,
  FaStar
} from 'react-icons/fa';
import { Link, useLocation } from 'react-router-dom';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeLink, setActiveLink] = useState('Home');
  const [showEnquiryForm, setShowEnquiryForm] = useState(false);
  
  // Get current location
  const location = useLocation();

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    country: '',
    service: '',
    message: '',
    agree: false
  });

  const [formSubmitted, setFormSubmitted] = useState(false);

  const countries = ['India', 'USA', 'UK', 'Canada', 'Australia', 'UAE', 'Singapore', 'Other'];
  
  const services = [
    'Furniture Consultation',
    'Interior Design',
    'Custom Furniture',
    'Home Decor',
    'Delivery Service',
    'After Sales Support'
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-open enquiry form on page load - ONLY FOR HOME PAGE
  useEffect(() => {
    // Check if current path is home page
    if (location.pathname === '/' || location.pathname === '/home') {
      setShowEnquiryForm(true);
    } else {
      setShowEnquiryForm(false);
    }
  }, [location.pathname]); // Re-run when location changes

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormSubmitted(true);
    setTimeout(() => {
      setFormSubmitted(false);
      setShowEnquiryForm(false);
      setFormData({
        name: '', email: '', phone: '', country: '', service: '', message: '', agree: false
      });
    }, 3000);
  };

  const navLinks = [
    { name: 'Home', path: '/home' },
    { name: 'Products', path: '/products' },
    { name: 'Collections', path: '/collections' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  const socialLinks = [
    { icon: <FaFacebookF />, href: '#', color: 'hover:bg-blue-600', label: 'Facebook' },
    { icon: <FaInstagram />, href: '#', color: 'hover:bg-pink-600', label: 'Instagram' },
    { icon: <FaTwitter />, href: '#', color: 'hover:bg-blue-400', label: 'Twitter' },
    { icon: <FaPinterest />, href: '#', color: 'hover:bg-red-600', label: 'Pinterest' },
  ];

  // Check if current page is home
  const isHomePage = location.pathname === '/' || location.pathname === '/home';

  return (
    <>
      {/* Top Bar */}
      <div className={`relative overflow-hidden transition-all duration-500 ${
        scrolled ? 'h-0 opacity-0' : 'h-auto opacity-100'
      }`}>
        <div className="absolute inset-0 bg-gradient-to-r from-pink-400 via-purple-400 to-blue-400 animate-gradient-x"></div>
        <div className="relative backdrop-blur-xl bg-white/10">
          <div className="container mx-auto px-4 py-2">
            <div className="flex flex-wrap justify-between items-center">
              {/* Phone Number */}
              <div className="group flex items-center space-x-3">
                <div className="relative">
                  <div className="absolute inset-0 bg-pink-400 rounded-full animate-ping opacity-75"></div>
                  <div className="relative bg-white rounded-full p-1.5">
                    <FaPhone className="text-pink-500 text-xs animate-wiggle" />
                  </div>
                </div>
                <span className="text-white font-medium text-sm tracking-wide">
                  <span className="hidden sm:inline">Call us: </span>
                  8573035759
                </span>
              </div>

              {/* Right Side - Social Media Icons */}
              <div className="flex items-center gap-3">
                <div className="flex space-x-2">
                  {socialLinks.map((social, index) => (
                    <a
                      key={index}
                      href={social.href}
                      aria-label={social.label}
                      className="relative group"
                    >
                      <div className={`relative w-8 h-8 rounded-full flex items-center justify-center
                        transition-all duration-500 hover:rotate-12 hover:scale-110
                        ${social.color} text-white`}
                        style={{
                          animation: `float 3s ease-in-out ${index * 0.2}s infinite`
                        }}
                      >
                        {social.icon}
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className={`sticky top-0 z-50 transition-all duration-500 ${
        scrolled 
          ? 'bg-white/95 backdrop-blur-xl shadow-2xl' 
          : 'bg-white shadow-lg'
      }`}>
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center h-20">
            
            {/* Logo */}
            <div className="relative group flex-shrink-0">
              <div className="absolute -inset-2 bg-gradient-to-r from-pink-500 to-blue-500 rounded-full opacity-0 group-hover:opacity-20 blur-md transition-all duration-500"></div>
              <div className="relative flex items-center justify-center">
                <img 
                  src="/logo.png" 
                  alt="Home Fashion Logo"
                  className="h-32 w-auto max-w-[140px] object-contain transform group-hover:scale-105 transition-all duration-500"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://via.placeholder.com/140x50?text=HF';
                  }}
                />
              </div>
            </div>

            {/* Navigation Links - CENTERED */}
            <div className="hidden md:flex items-center justify-center flex-1 space-x-1">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  onClick={() => setActiveLink(link.name)}
                  className={`relative px-4 py-2 group overflow-hidden rounded-lg transition-all duration-300
                    ${activeLink === link.name 
                      ? 'text-pink-500 font-semibold' 
                      : 'text-gray-700 hover:text-pink-500 font-medium'
                    }`}
                >
                  <span className="absolute inset-0 bg-gradient-to-r from-pink-50 to-blue-50 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left rounded-lg"></span>
                  <span className="relative text-sm lg:text-base tracking-wide">
                    {link.name}
                  </span>
                  <span className={`absolute bottom-0 left-3 right-3 h-0.5 bg-gradient-to-r from-pink-500 to-blue-500 
                    transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500
                    ${activeLink === link.name ? 'scale-x-100' : ''}`}>
                  </span>
                </Link>
              ))}
            </div>

            {/* Enquiry Button - Only show on Home Page */}
            {isHomePage && (
              <div className="hidden md:flex items-center">
                <button
                  onClick={() => setShowEnquiryForm(true)}
                  className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-pink-500 to-blue-500 text-white rounded-full text-sm font-semibold hover:shadow-xl transform hover:scale-105 transition-all duration-300 border border-white/20"
                >
                  <FaEnvelope size={14} />
                  Enquiry Form
                </button>
              </div>
            )}

            {/* Mobile Menu Button and Enquiry - Only show Enquiry on Home Page */}
            <div className="flex items-center gap-2 md:hidden">
              {isHomePage && (
                <button
                  onClick={() => setShowEnquiryForm(true)}
                  className="w-10 h-10 bg-gradient-to-r from-pink-500 to-blue-500 rounded-full flex items-center justify-center text-white hover:shadow-lg transition-all duration-300"
                >
                  <FaEnvelope size={16} />
                </button>
              )}
              <button
                onClick={toggleMenu}
                className="relative w-10 h-10 flex items-center justify-center group"
                aria-label="Toggle menu"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-pink-500 to-blue-500 rounded-full opacity-0 group-hover:opacity-20 transition-opacity duration-300"></span>
                <div className="relative transform transition-transform duration-500 group-hover:rotate-180">
                  {isOpen ? (
                    <FaTimes className="text-xl text-pink-500" />
                  ) : (
                    <FaBars className="text-xl text-blue-500" />
                  )}
                </div>
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          <div
            className={`md:hidden transition-all duration-500 ease-in-out overflow-hidden ${
              isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            <div className="py-4 space-y-2">
              {navLinks.map((link, index) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className="relative block overflow-hidden group"
                  onClick={() => {
                    setActiveLink(link.name);
                    setIsOpen(false);
                  }}
                  style={{
                    animation: isOpen ? `slideIn 0.5s ease-out ${index * 0.1}s both` : 'none'
                  }}
                >
                  <span className="absolute inset-0 bg-gradient-to-r from-pink-500 to-blue-500 transform -translate-x-full group-hover:translate-x-0 transition-transform duration-500"></span>
                  <span className="relative flex items-center px-4 py-3 text-gray-700 group-hover:text-white transition-colors duration-500 text-base">
                    {link.name}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </nav>

      {/* ===== ENQUIRY FORM - ONLY SHOW ON HOME PAGE ===== */}
      {showEnquiryForm && isHomePage && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/30 animate-fadeIn">
          {/* Fixed size form - NO SCROLLING */}
          <div className="relative bg-gradient-to-b from-white to-pink-50 rounded-2xl shadow-2xl w-[400px] mt-16 animate-scaleIn">
            
            {/* Decorative Header */}
            <div className="absolute top-0 left-0 right-0 h-16 bg-gradient-to-r from-pink-500 to-blue-500 rounded-t-2xl"></div>
            
            {/* Close Button */}
            <button
              onClick={() => setShowEnquiryForm(false)}
              className="absolute top-2 right-2 w-6 h-6 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/40 hover:scale-110 transition-all duration-300 z-20 border border-white/30"
            >
              <FaTimes size={12} />
            </button>

            {/* Form Content - FIXED HEIGHT, NO SCROLLING */}
            <div className="relative z-10 p-4 pt-12">
              {/* Icon and Title */}
              <div className="text-center mb-3">
                <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-1 shadow-lg">
                  <FaEnvelope className="text-white text-lg" />
                </div>
                <h2 className="text-lg font-bold">
                  <span className="bg-gradient-to-r from-pink-500 to-blue-500 bg-clip-text text-transparent">
                    Enquiry Form
                  </span>
                </h2>
                <p className="text-gray-500 text-[9px]">We'll get back to you within 24 hours</p>
              </div>
              
              {formSubmitted ? (
                <div className="text-center py-4 bg-white rounded-lg shadow-inner">
                  <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-1 animate-bounce">
                    <FaCheckCircle className="w-7 h-7 text-green-500" />
                  </div>
                  <h3 className="text-base font-bold text-gray-800 mb-0.5">Thank You!</h3>
                  <p className="text-[10px] text-gray-600">Your enquiry has been submitted successfully.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-2">
                  {/* Your Name */}
                  <div>
                    <label className="block text-gray-700 text-[9px] font-semibold mb-0.5 ml-1">Your Name <span className="text-pink-500">*</span></label>
                    <input 
                      type="text" 
                      name="name" 
                      value={formData.name} 
                      onChange={handleChange} 
                      required
                      className="w-full px-2 py-1.5 text-[10px] rounded-lg border border-gray-200 focus:border-pink-500 focus:ring-1 focus:ring-pink-200"
                      placeholder="John Doe" 
                    />
                  </div>

                  {/* Your Email id */}
                  <div>
                    <label className="block text-gray-700 text-[9px] font-semibold mb-0.5 ml-1">Your Email id <span className="text-pink-500">*</span></label>
                    <input 
                      type="email" 
                      name="email" 
                      value={formData.email} 
                      onChange={handleChange} 
                      required
                      className="w-full px-2 py-1.5 text-[10px] rounded-lg border border-gray-200 focus:border-pink-500 focus:ring-1 focus:ring-pink-200"
                      placeholder="john@example.com" 
                    />
                  </div>

                  {/* Number */}
                  <div>
                    <label className="block text-gray-700 text-[9px] font-semibold mb-0.5 ml-1">Number <span className="text-pink-500">*</span></label>
                    <input 
                      type="tel" 
                      name="phone" 
                      value={formData.phone} 
                      onChange={handleChange} 
                      required
                      className="w-full px-2 py-1.5 text-[10px] rounded-lg border border-gray-200 focus:border-pink-500 focus:ring-1 focus:ring-pink-200"
                      placeholder="+91 98765 43210" 
                    />
                  </div>

                  {/* Select Country & Services in Grid */}
                  <div className="grid grid-cols-2 gap-1">
                    <div>
                      <label className="block text-gray-700 text-[9px] font-semibold mb-0.5 ml-1">Country</label>
                      <select 
                        name="country" 
                        value={formData.country} 
                        onChange={handleChange}
                        className="w-full px-1.5 py-1.5 text-[9px] rounded-lg border border-gray-200 focus:border-pink-500 appearance-none bg-white"
                      >
                        <option value="">Select</option>
                        {countries.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-gray-700 text-[9px] font-semibold mb-0.5 ml-1">Services</label>
                      <select 
                        name="service" 
                        value={formData.service} 
                        onChange={handleChange}
                        className="w-full px-1.5 py-1.5 text-[9px] rounded-lg border border-gray-200 focus:border-pink-500 appearance-none bg-white"
                      >
                        <option value="">Select</option>
                        {services.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-gray-700 text-[9px] font-semibold mb-0.5 ml-1">Message <span className="text-pink-500">*</span></label>
                    <textarea 
                      name="message" 
                      value={formData.message} 
                      onChange={handleChange} 
                      required
                      rows="1" 
                      className="w-full px-2 py-1 text-[10px] rounded-lg border border-gray-200 focus:border-pink-500"
                      placeholder="Your requirements..."
                    ></textarea>
                  </div>

                  {/* Terms and Conditions */}
                  <div className="bg-gradient-to-r from-pink-50 to-blue-50 p-1.5 rounded-lg border border-pink-100">
                    <label className="flex items-start gap-1 cursor-pointer">
                      <input 
                        type="checkbox" 
                        name="agree" 
                        checked={formData.agree} 
                        onChange={handleChange} 
                        required
                        className="mt-0.5 w-3 h-3 text-pink-500 rounded border-pink-300" 
                      />
                      <span className="text-[7px] text-gray-600 leading-tight">
                        I agree to Terms & Privacy Policy. I want to receive updates via Email, SMS, WhatsApp.
                      </span>
                    </label>
                  </div>

                  {/* Submit Button */}
                  <button 
                    type="submit"
                    className="w-full py-2 bg-gradient-to-r from-pink-500 to-blue-500 text-white rounded-lg font-semibold text-[10px] hover:shadow-lg transform hover:scale-105 transition-all duration-300 flex items-center justify-center gap-1"
                  >
                    <FaWhatsapp className="text-xs" />
                    Submit Enquiry
                  </button>

                  {/* Contact Info */}
                  <div className="flex items-center justify-center gap-2 text-[7px] text-gray-500">
                    <span className="flex items-center gap-0.5">
                      <FaHeadset className="text-pink-400 text-[8px]" /> 24/7 Support
                    </span>
                    <span className="flex items-center gap-0.5">
                      <FaClock className="text-blue-400 text-[8px]" /> Quick Response
                    </span>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;